import type { i18n as I18nInstance } from 'i18next';
import type {
  BasketTableAnswerItem,
  EducationContent,
  QuestionConfigBranchLoaderProps,
  QuestionConfigQuestionType,
} from '/index';

import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1Ver2 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1Ver2';
import educationContentJson from '/business/shared/components/questions/scope1/_education/refrigerants.json';
import useBuildingRefrigerantsSchema from '/business/shared/components/questions/scope1/_hooks/useBuildingRefrigerantsSchema';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import SnowflakeIcon from '/business/shared/images/icons/icon-snowflake.svg';
import initiativeCardBackground from '/business/shared/images/initiatives/Refrigerant-card-1x.jpg';
import initiativeBackground from '/business/shared/images/initiatives/Refrigerant-card-2x.jpg';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';

interface ConfigureRefrigerantsOptions {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
}

type RefrigerantQuantityRow = {
  quantity: number;
  refrigerantUnit?: string;
};

type RefrigerantTonnageRow = {
  tonnage: number;
};

function isEducationContent(value: object): value is EducationContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'topParas' in value &&
    Array.isArray(value.topParas) &&
    'exampleParas' in value &&
    Array.isArray(value.exampleParas)
  );
}

function getEducationContent(): EducationContent {
  if (!isEducationContent(educationContentJson)) {
    throw new Error('Refrigerants education content is not configured');
  }
  return educationContentJson;
}

export const getConfigureRefrigerants = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = ['slide1', 'slide2', 'slide3', 'slide4'],
}: ConfigureRefrigerantsOptions): QuestionConfigQuestionType => {
  return {
    id: 'refrigerants',
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_1
      : questionScopeCategories.BUILDING_EMISSIONS,
    isCompletePropName: 'refrigerants',
    apiKey: 'calculation-building-emissions-refrigerants',
    title: i18n.t('business:Questions.calculations.question2.title'),
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.refrigerants',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.refrigerantsTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.refrigerants',
    reductionPageKey: 'refrigerants',
    reductionPageCards: reductionCardsSlides,
    icon: SnowflakeIcon,
    useHookLoader: useBuildingRefrigerantsSchema,
    useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatSharedSupplierScope1Ver2({
        ...props,
        questionFormatName: 'refrigerants',
        emissionSourcePlaceholder: 'Select refrigerant',
      }),
    useQuestionBranchCustomLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatSharedCustomScope1({ ...props, questionFormatName: 'refrigerants' }),
    initiative: {
      formatForInitiativeSummary: () => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: false,
          category: false,
          description: false,
          descriptionNoReduction: false,
        };
      },
      getVisuals: ({ isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
      },
    },
    table: {
      title: i18n.t('business:Questions.calculations.question2.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.question2.table.heading1'),
        i18n.t('business:Questions.calculations.question2.table.heading2'),
        i18n.t('business:Questions.calculations.question2.table.heading3'),
      ],
      cells: [
        'refrigerantType',
        ({ quantity, refrigerantUnit = 'units' }: RefrigerantQuantityRow) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(refrigerantUnit), refrigerantUnit)}`,
        ({ tonnage }: RefrigerantTonnageRow) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      isAnswerEditable: false,
      supplier: {
        columns: [
          'emissionSource',
          'supplierName',
          'supplierIntensityDataOrAbsoluteEmissions',
          'quantity',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    details: {
      title: 'refrigerantType',
      items: [
        'refrigerantType',
        ({ quantity, refrigerantUnit = 'units' }: RefrigerantQuantityRow) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(refrigerantUnit), refrigerantUnit)}`,
        ({ tonnage }: RefrigerantTonnageRow) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.question2.details.title'),
        },
      ],
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with the top up or recharge of refrigerants by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: i18n.t('business:Questions.selectors.refrigerants.activity.requiredData'),
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData: i18n.t('business:Questions.selectors.refrigerants.supplier.requiredData'),
        },
        {
          title: questionSelectorOptions.CUSTOM,
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: getEducationContent(),
      bottomContentsSupplier: bottomContentsSupplier,
    },
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about refrigerants that have been topped up or recharged by your business during the reporting
          period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about refrigerants that have been topped up or recharged by your business during the reporting
          period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
